import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
    Download,
    FileText,
    Receipt,
    CalendarDays,
    Phone,
    MessageSquare,
} from "lucide-react";
import { buildEnquiry, waUrl, telUrl } from "@/lib/enquiry";

const WhatsAppGlyph = ({ size = 15 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.372-.025-.521-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.885-9.885 9.885M20.52 3.449C18.24 1.245 15.24 0 12.045 0 5.463 0 .104 5.359.101 11.945c0 2.096.549 4.142 1.595 5.945L0 24l6.335-1.652a11.96 11.96 0 005.71 1.454h.006c6.585 0 11.946-5.359 11.949-11.945a11.9 11.9 0 00-3.48-8.4z" />
    </svg>
);

const base =
    "group inline-flex items-center justify-center gap-2 px-6 py-3.5 text-[11px] uppercase tracking-[0.15em] transition-colors duration-300";
const filled = `${base} bg-orange text-white hover:bg-orange-dark`;
const outline = `${base} border border-charcoal/25 text-charcoal hover:border-orange hover:text-orange`;
const onImage = `${base} border border-white/40 text-white hover:bg-white hover:text-charcoal`;

export const CTAButtons = ({ project, keys = ["whatsapp", "sitevisit"], onImage: img = false, className = "" }) => {
    const navigate = useNavigate();
    const name = project?.name;
    const secondary = img ? onImage : outline;

    const openWa = (intent) => window.open(waUrl(buildEnquiry(name, intent)), "_blank", "noopener,noreferrer");
    const download = (intent, label) => {
        if (project?.brochure && project.brochure !== "#" && intent === "brochure") {
            window.open(project.brochure, "_blank", "noopener,noreferrer");
            return;
        }
        openWa(intent);
        toast.success(`We'll send the ${label.toLowerCase()} on WhatsApp shortly.`);
    };

    const defs = {
        whatsapp: {
            testid: "cta-whatsapp",
            className: filled,
            icon: <WhatsAppGlyph />,
            label: "WhatsApp Now",
            onClick: () => openWa("general"),
        },
        sitevisit: {
            testid: "cta-sitevisit",
            className: filled,
            icon: <CalendarDays size={15} />,
            label: "Book Site Visit",
            onClick: () => openWa("sitevisit"),
        },
        brochure: {
            testid: "cta-brochure",
            className: secondary,
            icon: <Download size={15} />,
            label: "Download Brochure",
            onClick: () => download("brochure", "Brochure"),
        },
        floorplan: {
            testid: "cta-floorplan",
            className: secondary,
            icon: <FileText size={15} />,
            label: "Download Floor Plan",
            onClick: () => download("floorplan", "Floor Plan"),
        },
        pricesheet: {
            testid: "cta-pricesheet",
            className: secondary,
            icon: <Receipt size={15} />,
            label: "Request Price Sheet",
            onClick: () => download("pricesheet", "Price Sheet"),
        },
        consultation: {
            testid: "cta-consultation",
            className: secondary,
            icon: <MessageSquare size={15} />,
            label: "Book Private Consultation",
            onClick: () => navigate("/#consultation"),
        },
        call: {
            testid: "cta-call",
            className: secondary,
            icon: <Phone size={15} />,
            label: "Call Now",
            onClick: () => (window.location.href = telUrl),
        },
    };

    return (
        <div className={`flex flex-wrap gap-3 ${className}`}>
            {keys.map((k) => {
                const b = defs[k];
                if (!b) return null;
                return (
                    <button
                        key={k}
                        data-testid={b.testid}
                        onClick={b.onClick}
                        className={b.className}
                    >
                        {b.icon}
                        {b.label}
                    </button>
                );
            })}
        </div>
    );
};
